import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_FILTERS = { status: '', type: '', startDate: '', endDate: '' };

const PARAM_KEYS = {
  search: 'q',
  status: 'status',
  type: 'type',
  startDate: 'from',
  endDate: 'to',
  page: 'page',
};

const parseParams = (searchParams) => ({
  search: searchParams.get(PARAM_KEYS.search) || '',
  filters: {
    status: searchParams.get(PARAM_KEYS.status) || '',
    type: searchParams.get(PARAM_KEYS.type) || '',
    startDate: searchParams.get(PARAM_KEYS.startDate) || '',
    endDate: searchParams.get(PARAM_KEYS.endDate) || '',
  },
  page: Math.max(parseInt(searchParams.get(PARAM_KEYS.page), 10) || 1, 1),
});

const buildParams = ({ search, filters, page }, prevParams) => {
  const next = new URLSearchParams(prevParams);
  const setOrDelete = (key, value) => (value ? next.set(key, value) : next.delete(key));

  setOrDelete(PARAM_KEYS.search, search);
  setOrDelete(PARAM_KEYS.status, filters.status);
  setOrDelete(PARAM_KEYS.type, filters.type);
  setOrDelete(PARAM_KEYS.startDate, filters.startDate);
  setOrDelete(PARAM_KEYS.endDate, filters.endDate);
  page > 1 ? next.set(PARAM_KEYS.page, String(page)) : next.delete(PARAM_KEYS.page);

  return next;
};

/**
 * URL is the single source of truth for search/filters/page.
 * `replace: true` keeps the tickets list as ONE history entry no matter how
 * many filters get applied — so browser Back from a ticket detail page
 * (a normal push) always lands back on this exact state.
 */
export function useTicketFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseParams(searchParams), [searchParams]);

  const updateUrl = useCallback(
    (nextState) => setSearchParams(buildParams(nextState, searchParams), { replace: true }),
    [searchParams, setSearchParams]
  );

  const setSearch = useCallback(
    (value) => updateUrl({ search: value, filters: state.filters, page: 1 }),
    [state.filters, updateUrl]
  );

  const setFilters = useCallback(
    (nextFilters) => updateUrl({ search: state.search, filters: nextFilters, page: 1 }),
    [state.search, updateUrl]
  );

  const removeFilter = useCallback(
    (key) => updateUrl({ search: state.search, filters: { ...state.filters, [key]: '' }, page: 1 }),
    [state.filters, state.search, updateUrl]
  );

  const resetFilters = useCallback(
    () => updateUrl({ search: state.search, filters: DEFAULT_FILTERS, page: 1 }),
    [state.search, updateUrl]
  );

  const setPage = useCallback(
    (page) => updateUrl({ search: state.search, filters: state.filters, page: Math.max(page, 1) }),
    [state.filters, state.search, updateUrl]
  );

  return {
    search: state.search,
    filters: state.filters,
    page: state.page,
    setSearch,
    setFilters,
    removeFilter,
    resetFilters,
    setPage,
  };
}
